import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, Subject, tap } from 'rxjs';

import { TemplateValues, fillTemplate } from '@core/filters/path-filter/templates';
import { FilterExpr } from '@core/filters/path-filter/wire';
import { OrderBy } from '@core/models/entity/entity-selector-request.model';
import {
  CreateFilterPresetRequest,
  FilterPresetModel,
  FilterPresetPayload,
  FilterPresetScope,
  FilterTemplateParameter
} from '@core/models/filter-preset.model';
import { hasAdminOrAbove } from '@core/models/user-role.constants';
import { FilterPresetsService } from '@core/services/filter-presets/filter-presets.service';
import { selectUser } from '@core/store/auth/auth.selectors';
import { FilterGrammar } from '@core/store/user-preferences/user-preferences.model';
import { isNil } from '@core/utils/shared-utils';

import {
  AvailableFilterField,
  FilterConfiguration
} from '@shared/components/filter-query-builder/filter.models';
import { NewParameterRequest } from '@shared/components/path-filter/path-filter.models';
import {
  RestoredSort,
  SortableFieldRef,
  resolveDefaultPreset,
  restoreFilterPresetPayload,
  storedPresetGrammar
} from '@shared/utils/filter-preset.utils';

/** One labelled section of the preset menu. Empty sections are never emitted. */
export interface FilterPresetGroup {
  labelKey: string;
  labelParams?: Record<string, string>;
  presets: FilterPresetModel[];
}

/** The state a preset resolves to, ready to be pushed into the entity table. */
export interface AppliedFilterPreset {
  filters?: FilterConfiguration;
  search?: string;
  /** Absent when the preset stores no sort, which leaves the table's order alone. */
  sort?: RestoredSort[];
  /**
   * Absent on a detach, and on a preset holding only free text and root sorts, which the bar
   * applies in the grammar it shows.
   */
  grammar?: FilterGrammar;
  pathFilter?: FilterExpr;
  /** Sorts the Path bar holds, related-entity ones included. */
  pathSort?: OrderBy[];
  /** Fields the entity no longer exposes, dropped while restoring. */
  droppedFields: string[];
  /** The active preset was deselected: the bar returns to the grammar of the preferences. */
  detached?: true;
  /** Applied on its own as the default when the presets loaded, not picked by the user. */
  automatic?: true;
}

/** A template applied with these values: what its pill shows and what reopening prefills. */
export interface ActiveTemplate {
  preset: FilterPresetModel;
  values: TemplateValues;
}

/**
 * Owns the saved filters of the entity currently on screen.
 *
 * Provided by `EntityComponent` rather than by the preset UI, because the filter bar the
 * UI lives in is only rendered while advanced filtering is open: a component mounted
 * there could never load a default preset and open that bar in the first place.
 */
@Injectable()
export class FilterPresetsStore {
  private readonly presetsService = inject(FilterPresetsService);
  private readonly store = inject(Store);

  private readonly user = this.store.selectSignal(selectUser);

  /** Context, driven by the host component. */
  readonly entityType = signal('');
  readonly dataSpaceId = signal<number | undefined>(undefined);
  /**
   * Field the host was opened from, as `OWNER.field_path`. Set by reference pickers only:
   * an entity list has no field to scope to, and must not see field-scoped presets.
   */
  readonly presetContext = signal<string | undefined>(undefined);
  /** Filterable fields of the entity, used to prune conditions on restore. */
  readonly availableFields = signal<AvailableFilterField[]>([]);
  /** Sortable columns of the entity, used to resolve a stored sort back to column keys. */
  readonly sortableFields = signal<SortableFieldRef[]>([]);
  /** What saving right now would store. */
  readonly currentPayload = signal<FilterPresetPayload>({});

  readonly presets = signal<FilterPresetModel[]>([]);
  /** Set by a host forced to Path: a Classic preset would clear its filter. */
  readonly pathOnly = signal(false);
  /** The presets the menu and the default auto-apply consider. */
  private readonly offered = computed(() =>
    this.pathOnly()
      ? this.presets().filter((preset) => storedPresetGrammar(preset) !== 'classic')
      : this.presets()
  );
  readonly activePresetId = signal<number | null>(null);
  /** On only where the bar can apply a Path tree; elsewhere a template would apply nothing. */
  readonly showsTemplates = signal(false);
  /** On while the Path panel composes a template: value slots may then hold `{{name}}` holes. */
  readonly templateAuthoring = signal(false);
  /** The composer's "New parameter" request, until the parameter is named or the prompt cancelled. */
  readonly templateParameterRequest = signal<NewParameterRequest | null>(null);
  /** Label typed per hole name; kept while a hole is removed and added back. */
  readonly templateLabels = signal<Record<string, string>>({});
  /** The template whose values are being asked for; it drives the activation popover. */
  readonly pendingTemplate = signal<FilterPresetModel | null>(null);
  readonly activeTemplate = signal<ActiveTemplate | null>(null);

  /** Emits whenever a preset (or a detach) must be pushed into the table. */
  readonly applied = new Subject<AppliedFilterPreset>();

  /** Context the loaded presets belong to, so a stale response is never applied. */
  private readonly loadedKey = signal<string | null>(null);
  private readonly autoAppliedKey = signal<string | null>(null);
  private readonly autoApplySuppressed = signal(false);

  private readonly contextKey = computed(
    () => `${this.entityType()}::${this.dataSpaceId() ?? ''}::${this.presetContext() ?? ''}`
  );

  /** Field half of `presetContext`, the part worth showing in a section header. */
  private readonly contextFieldLabel = computed(() => {
    const context = this.presetContext();
    return isNil(context) ? '' : context.slice(context.indexOf('.') + 1);
  });

  /**
   * The menu's sections, field-scoped first. The host renders whatever comes out, so an
   * entity list keeps its two sections without knowing contexts exist.
   *
   * A preset carrying any context matches the current one: the API only ever returns the
   * entity-wide presets plus those of the context it was asked for.
   */
  readonly presetGroups = computed<FilterPresetGroup[]>(() => {
    const presets = this.offered();
    const field = this.contextFieldLabel();
    const bucket = (scope: FilterPresetScope, fieldScoped: boolean): FilterPresetModel[] =>
      presets.filter(
        (preset) =>
          !preset.template &&
          preset.scope === scope &&
          !isNil(preset.context) === fieldScoped
      );

    const groups: FilterPresetGroup[] = [
      ...(field.length === 0
        ? []
        : [
            {
              labelKey: 'features.data.filter_presets.context_mine',
              labelParams: { field },
              presets: bucket(FilterPresetScope.USER, true)
            },
            {
              labelKey: 'features.data.filter_presets.context_shared',
              labelParams: { field },
              presets: bucket(FilterPresetScope.DATASPACE, true)
            }
          ]),
      {
        labelKey: 'features.data.filter_presets.mine',
        presets: bucket(FilterPresetScope.USER, false)
      },
      {
        labelKey: 'features.data.filter_presets.shared',
        presets: bucket(FilterPresetScope.DATASPACE, false)
      },
      {
        labelKey: 'features.data.filter_presets.templates',
        presets: this.showsTemplates() ? presets.filter((preset) => preset.template) : []
      }
    ];

    return groups.filter((group) => group.presets.length > 0);
  });

  readonly activePreset = computed(
    () => this.presets().find((preset) => preset.id === this.activePresetId()) ?? null
  );

  /** Sharing needs both the permission and a dataspace to attach the preset to. */
  readonly canShareWithDataspace = computed(
    () => hasAdminOrAbove(this.user()?.role) && !isNil(this.dataSpaceId())
  );

  /** Whether the save dialog can offer to bind the preset to the field in play. */
  readonly canSaveForContext = computed(() => !isNil(this.presetContext()));
  readonly contextField = this.contextFieldLabel;

  readonly hasFiltersToSave = computed(() => {
    if (this.templateAuthoring()) {
      return false;
    }
    const payload = this.currentPayload();
    return !isNil(payload.filters) || !isNil(payload.search) || !isNil(payload.pathFilter);
  });

  /** Whether the save dialog has a sort worth offering to store. */
  readonly hasSortToSave = computed(() => (this.currentPayload().sort?.length ?? 0) > 0);

  constructor() {
    effect(() => {
      const entityType = this.entityType();
      const dataSpaceId = this.dataSpaceId();
      const presetContext = this.presetContext();
      const key = this.contextKey();
      // The router reuses the host across entities: a template kept from the previous one would
      // stay on the pill, or be applied, over a table it was not written for.
      untracked(() => {
        this.pendingTemplate.set(null);
        this.activeTemplate.set(null);
      });
      if (entityType.length === 0) {
        return;
      }
      untracked(() => this.load(entityType, dataSpaceId, presetContext, key));
    });

    // Auto-apply waits for the entity's filterable fields: pruning against an empty list
    // would strip every column condition the default preset carries.
    effect(() => {
      const key = this.contextKey();
      const presets = this.offered();
      const hasFields = this.availableFields().length > 0;
      if (!hasFields || this.loadedKey() !== key || this.autoAppliedKey() === key) {
        return;
      }
      const suppressed = this.autoApplySuppressed();
      const presetContext = this.presetContext();
      untracked(() => {
        this.autoAppliedKey.set(key);
        if (suppressed) {
          return;
        }
        const defaultPreset = resolveDefaultPreset(presets, presetContext);
        if (!isNil(defaultPreset)) {
          this.apply(defaultPreset, true);
        }
      });
    });
  }

  private load(
    entityType: string,
    dataSpaceId: number | undefined,
    presetContext: string | undefined,
    key: string
  ): void {
    this.presetsService.list$(entityType, dataSpaceId, presetContext).subscribe({
      next: (presets) => {
        this.presets.set(presets);
        this.loadedKey.set(key);
      },
      error: () => {
        this.presets.set([]);
        this.loadedKey.set(key);
      }
    });
  }

  private reload(): void {
    this.load(
      this.entityType(),
      this.dataSpaceId(),
      this.presetContext(),
      this.contextKey()
    );
  }

  /**
   * Applies a preset, or detaches the active one when passed `null`. `automatic`: the default,
   * applied because the presets loaded rather than picked by the user.
   */
  apply(preset: FilterPresetModel | null, automatic = false): void {
    if (isNil(preset)) {
      this.activePresetId.set(null);
      this.activeTemplate.set(null);
      this.applied.next({ droppedFields: [], detached: true });
      return;
    }
    // A template's tree still holds its holes: it reaches the table only once filled in.
    if (preset.template) {
      this.pendingTemplate.set(preset);
      return;
    }
    this.activeTemplate.set(null);
    const restored = restoreFilterPresetPayload(
      preset,
      this.availableFields(),
      this.sortableFields()
    );
    this.activePresetId.set(preset.id);
    this.applied.next({
      ...restored,
      grammar: storedPresetGrammar(preset),
      ...(automatic ? { automatic: true } : {})
    });
  }

  /**
   * Re-installs the active-preset marker of a restored filter state, and stops the default
   * preset from being auto-applied over it.
   *
   * The host has already pushed the restored filters into the table, so applying the
   * default preset on top — which the load effect would otherwise do as soon as the
   * presets arrive — would silently discard them. The store lives for one mount of one
   * list, so suppressing for good is enough.
   */
  seedRestoredState(activePresetId: number | null): void {
    this.activePresetId.set(activePresetId);
    this.suppressDefaultPreset();
  }

  /** For a host that seeds its own filter: the default preset would replace it once loaded. */
  suppressDefaultPreset(): void {
    this.autoApplySuppressed.set(true);
  }

  /**
   * Drops the active-preset marker while leaving the filters untouched.
   *
   * Called when the user edits the criteria by hand: the chip must stop claiming those
   * filters come from that preset, but the edit itself stands. Distinct from
   * `apply(null)`, which also clears the filters.
   */
  detach(): void {
    this.activePresetId.set(null);
    this.activeTemplate.set(null);
  }

  endTemplateAuthoring(): void {
    this.templateAuthoring.set(false);
    this.templateParameterRequest.set(null);
    this.templateLabels.set({});
  }

  /** Applies a template filled in with `values`; `false`, and nothing applied, while one is blank. */
  activateTemplate(preset: FilterPresetModel, values: TemplateValues): boolean {
    const pathFilter = fillTemplate(preset.pathFilter, preset.parameters ?? [], values);
    if (isNil(pathFilter)) {
      return false;
    }
    const restored = restoreFilterPresetPayload(
      preset,
      this.availableFields(),
      this.sortableFields()
    );
    this.pendingTemplate.set(null);
    this.activePresetId.set(preset.id);
    this.activeTemplate.set({ preset, values });
    this.applied.next({ ...restored, pathFilter, grammar: 'path' });
    return true;
  }

  cancelActivation(): void {
    this.pendingTemplate.set(null);
  }

  reopenActivation(): void {
    const active = this.activeTemplate();
    if (!isNil(active)) {
      this.pendingTemplate.set(active.preset);
    }
  }

  /**
   * Stores the hole-bearing tree as a template, then asks for its values so the table moves
   * straight on to the concrete filter. Templates are entity-wide and never the default; the
   * opted-in sort is kept as a preset keeps it.
   */
  saveTemplate(
    name: string,
    scope: FilterPresetScope,
    pathFilter: FilterExpr,
    parameters: FilterTemplateParameter[],
    includeSort: boolean
  ): Observable<FilterPresetModel> {
    const payload = this.currentPayload();
    const base = {
      name,
      entityType: this.entityType(),
      template: true,
      pathFilter,
      parameters,
      ...(includeSort && payload.sort?.length ? { sort: payload.sort } : {})
    };
    const dataSpaceId = this.dataSpaceId();
    const request: CreateFilterPresetRequest =
      scope === FilterPresetScope.DATASPACE && !isNil(dataSpaceId)
        ? { ...base, scope: FilterPresetScope.DATASPACE, dataSpaceId }
        : { ...base, scope: FilterPresetScope.USER };

    return this.presetsService.create$(request).pipe(
      tap((created) => {
        this.endTemplateAuthoring();
        this.reload();
        // Held back for its values even if the answer does not echo the flag.
        this.apply({ ...created, template: true });
      })
    );
  }

  /**
   * `contextScoped` binds the preset to the field in play. It is ignored without a context
   * — an entity list has no field to bind to.
   */
  saveNew(
    name: string,
    scope: FilterPresetScope,
    isDefault: boolean,
    includeSort: boolean,
    contextScoped = false
  ): Observable<FilterPresetModel> {
    const payload = this.currentPayload();
    const presetContext = this.presetContext();
    const base = {
      name,
      entityType: this.entityType(),
      ...(contextScoped && !isNil(presetContext) ? { context: presetContext } : {}),
      isDefault,
      ...(isNil(payload.filters) ? {} : { filters: payload.filters }),
      ...(isNil(payload.search) ? {} : { search: payload.search }),
      ...(isNil(payload.pathFilter) ? {} : { pathFilter: payload.pathFilter }),
      ...(includeSort && payload.sort?.length ? { sort: payload.sort } : {})
    };
    const dataSpaceId = this.dataSpaceId();
    const request: CreateFilterPresetRequest =
      scope === FilterPresetScope.DATASPACE && !isNil(dataSpaceId)
        ? { ...base, scope: FilterPresetScope.DATASPACE, dataSpaceId }
        : { ...base, scope: FilterPresetScope.USER };

    return this.presetsService.create$(request).pipe(
      tap((preset) => {
        this.activePresetId.set(preset.id);
        this.reload();
      })
    );
  }

  /**
   * Replaces a preset's stored criteria with the live ones.
   *
   * The sort key is only sent when the preset already carries one: storing a sort is a
   * choice made when the preset was created, and overwriting its criteria is not the
   * place to reverse it in either direction.
   */
  saveOver(preset: FilterPresetModel): Observable<FilterPresetModel> {
    // An overwrite would replace the template's holes with the live tree.
    if (preset.template) {
      return EMPTY;
    }
    const payload = this.currentPayload();
    return this.presetsService
      .update$(preset.id, {
        filters: payload.filters ?? null,
        search: payload.search ?? null,
        pathFilter: payload.pathFilter ?? null,
        ...(isNil(preset.sort) ? {} : { sort: payload.sort ?? null })
      })
      .pipe(
        tap((updated) => {
          this.activePresetId.set(updated.id);
          this.reload();
        })
      );
  }

  rename(preset: FilterPresetModel, name: string): Observable<FilterPresetModel> {
    return this.presetsService.update$(preset.id, { name }).pipe(tap(() => this.reload()));
  }

  /**
   * Promoting demotes the previous default in the same bucket server-side, so the list is
   * reloaded rather than patched locally.
   */
  toggleDefault(preset: FilterPresetModel): Observable<FilterPresetModel> {
    // gbe refuses a default template.
    if (preset.template) {
      return EMPTY;
    }
    return this.presetsService
      .update$(preset.id, { isDefault: !preset.isDefault })
      .pipe(tap(() => this.reload()));
  }

  remove(preset: FilterPresetModel): Observable<void> {
    return this.presetsService.delete$(preset.id).pipe(
      tap(() => {
        if (this.activePresetId() === preset.id) {
          this.apply(null);
        }
        this.reload();
      })
    );
  }
}
